import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDelete, apiGet, apiPost, errOf } from '../../lib/api';

interface ItemRow {
  id: string;
  item: string;
  budget_amount: number;
  actual_amount: number;
  category: string;
  note: string;
}
interface Totals { budget: number; actual: number; variance: number }
interface Summary {
  expense: Totals;
  income: Totals;
  other: Totals;
  externalIncome: number;
  netBudget: number;
  netActual: number;
  count: number;
}

const field = { width: '100%', borderRadius: 8, padding: '6px 10px', border: '1px solid var(--color-divider)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--font-size-small)' };
const btn = { background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: 'none', cursor: 'pointer' } as const;
const box = { background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' } as const;
const money = (n: number): string => `HK$${Number(n || 0).toLocaleString()}`;

/** 管理台「對數」—— 預算 vs 實際 ＋ **贊助已收款** → 盈餘／赤字 */
export default function AdminBudgetPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<ItemRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [sponsorPaid, setSponsorPaid] = useState(0);
  const [draft, setDraft] = useState({ item: '', budget_amount: '', actual_amount: '', category: 'expense' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const r = await apiGet<{ items: ItemRow[]; summary: Summary; sponsorPaid: number }>('/api/admin/budget');
    if (r.ok && r.data) {
      setItems(r.data.items);
      setSummary(r.data.summary);
      setSponsorPaid(r.data.sponsorPaid);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function add() {
    if (!draft.item.trim()) return;
    setBusy(true);
    setErr('');
    const r = await apiPost('/api/admin/budget', {
      item: draft.item.trim(),
      budget_amount: Number(draft.budget_amount) || 0,
      actual_amount: Number(draft.actual_amount) || 0,
      category: draft.category,
    });
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('bg_failed'));
      return;
    }
    setDraft({ item: '', budget_amount: '', actual_amount: '', category: 'expense' });
    setMsg(t('bg_saved'));
    await load();
  }

  async function remove(id: string) {
    if (!window.confirm(t('confirm_delete'))) return;
    const r = await apiDelete(`/api/admin/budget/${id}`);
    if (!r.ok) setErr(errOf(r.data) || t('bg_failed'));
    else await load();
  }

  const net = summary?.netActual ?? 0;

  return (
    <div className="max-w-3xl">
      <h2 className="font-bold text-lg mb-1">{t('admin_budget')}</h2>
      <p className="text-xs mb-3" style={{ color: 'var(--color-text-secondary)' }}>
        {t('bg_intro')}
      </p>
      {msg && <p className="text-sm mb-2" style={{ color: 'var(--color-primary)' }}>{msg}</p>}
      {err && <p className="text-sm mb-2" style={{ color: 'var(--color-danger)' }}>{err}</p>}

      {summary && (
        <section className="rounded-xl p-3 mb-5" style={box}>
          <h3 className="font-bold mb-2">{t('bg_summary')}</h3>
          <table className="text-sm w-full">
            <thead style={{ color: 'var(--color-text-secondary)' }}>
              <tr>
                <th className="text-left">{t('bg_category')}</th>
                <th className="text-right">{t('bg_budget')}</th>
                <th className="text-right">{t('bg_actual')}</th>
                <th className="text-right">{t('bg_variance')}</th>
              </tr>
            </thead>
            <tbody>
              {([
                ['bg_expense', summary.expense],
                ['bg_income', summary.income],
                ['bg_other', summary.other],
              ] as const).map(([k, v]) => (
                <tr key={k}>
                  <td>{t(k)}</td>
                  <td className="text-right">{money(v.budget)}</td>
                  <td className="text-right">{money(v.actual)}</td>
                  <td className="text-right" style={{ color: v.variance > 0 ? 'var(--color-danger)' : undefined }}>
                    {money(v.variance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-sm mt-2">
            {t('bg_sponsor_paid')}：<b>{money(sponsorPaid)}</b>
          </p>
          <p className="text-sm mt-1">
            {t('bg_net_budget')}：<b>{money(summary.netBudget)}</b> ｜ {t('bg_net_actual')}：
            <b style={{ color: net < 0 ? 'var(--color-danger)' : 'var(--color-primary)' }}>
              {money(net)}（{net < 0 ? t('bg_deficit') : t('bg_surplus')}）
            </b>
          </p>
        </section>
      )}

      <section className="rounded-xl p-3 mb-5" style={box}>
        <h3 className="font-bold mb-2">
          {t('bg_items')}（{items.length}）
        </h3>
        <ul className="text-sm mb-2">
          {items.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            items.map((x) => (
              <li key={x.id} className="flex items-center justify-between gap-2 py-1">
                <span>
                  {x.item} ｜ {t(`bg_cat_${x.category}`)} ｜ {money(x.budget_amount)} → {money(x.actual_amount)}
                </span>
                <button
                  type="button"
                  onClick={() => void remove(x.id)}
                  className="text-xs"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
                >
                  {t('btn_delete')}
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="grid grid-cols-4 gap-2">
          <input style={field} placeholder={t('bg_item')} value={draft.item} onChange={(e) => setDraft({ ...draft, item: e.target.value })} />
          <input style={field} inputMode="numeric" placeholder={t('bg_budget')} value={draft.budget_amount} onChange={(e) => setDraft({ ...draft, budget_amount: e.target.value.replace(/[^0-9]/g, '') })} />
          <input style={field} inputMode="numeric" placeholder={t('bg_actual')} value={draft.actual_amount} onChange={(e) => setDraft({ ...draft, actual_amount: e.target.value.replace(/[^0-9]/g, '') })} />
          <select style={field} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
            <option value="expense">{t('bg_cat_expense')}</option>
            <option value="income">{t('bg_cat_income')}</option>
            <option value="other">{t('bg_cat_other')}</option>
          </select>
        </div>
        <button type="button" disabled={busy} onClick={() => void add()} className="rounded-lg px-4 py-1.5 text-sm font-semibold mt-2" style={btn}>
          {t('bg_new')}
        </button>
        <p className="text-xs mt-2" style={{ color: 'var(--color-text-secondary)' }}>
          {t('bg_hint')}
        </p>
      </section>
    </div>
  );
}
